/** Fallback when registration-config has not loaded or omits mpin_digits. */
export const DEFAULT_MPIN_DIGITS = 4;

const MIN_MPIN_DIGITS = 4;
const MAX_MPIN_DIGITS = 8;

/**
 * Normalize auth.mpin_digits from registration-config.
 * Accepts 4–8; falls back to DEFAULT_MPIN_DIGITS.
 */
export function normalizeMpinDigits(value) {
  const digits = Number(value);
  if (
    Number.isInteger(digits) &&
    digits >= MIN_MPIN_DIGITS &&
    digits <= MAX_MPIN_DIGITS
  ) {
    return digits;
  }
  return DEFAULT_MPIN_DIGITS;
}

export function getMpinDigitsFromAuth(auth = {}) {
  return normalizeMpinDigits(auth?.mpin_digits);
}

export function createEmptyPin(length = DEFAULT_MPIN_DIGITS) {
  const digits = normalizeMpinDigits(length);
  return new Array(digits).fill("");
}

/** Stable UI order for registration fields returned by the API. */
export const REGISTRATION_FIELD_ORDER = [
  "username",
  "email",
  "phone",
  "first_name",
  "last_name",
  "date_of_birth",
  "country",
  "state",
  "city",
  "password",
  "referral_code",
];

export const REGISTRATION_FIELD_LABELS = {
  username: "Username",
  email: "Email",
  phone: "Mobile",
  first_name: "First Name",
  last_name: "Last Name",
  date_of_birth: "Date of Birth",
  country: "Country",
  state: "State",
  city: "City",
  password: "Password",
  referral_code: "Referral Code",
};

/** Humanized fallback label for a field the UI has no entry for. */
function humanizeFieldKey(key = "") {
  return String(key)
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Label for a field: API-supplied first, then the known map, then the key. */
export function getRegistrationFieldLabel(key, config = {}) {
  return (
    config?.label ||
    config?.display_name ||
    config?.title ||
    REGISTRATION_FIELD_LABELS[key] ||
    humanizeFieldKey(key)
  );
}

/**
 * Fields from registration-config in UI order: the known ones first, then any
 * custom field the API sent, in the order it sent them. Nothing is dropped.
 */
export function getOrderedRegistrationFields(fields = {}) {
  const known = REGISTRATION_FIELD_ORDER.filter((key) => fields[key]);
  const custom = Object.keys(fields).filter(
    (key) => fields[key] && !REGISTRATION_FIELD_ORDER.includes(key)
  );

  return [...known, ...custom].map((key) => {
    const config = fields[key];
    return {
      key,
      ...(config && typeof config === "object" ? config : {}),
    };
  });
}

/** Strong password rules shown live during signup. */
export const PASSWORD_VALIDATION_RULES = [
  {
    key: "minLength",
    label: "At least 8 characters",
    test: (password) => password.length >= 8,
  },
  {
    key: "uppercase",
    label: "At least one uppercase letter",
    test: (password) => /[A-Z]/.test(password),
  },
  {
    key: "lowercase",
    label: "At least one lowercase letter",
    test: (password) => /[a-z]/.test(password),
  },
  {
    key: "number",
    label: "At least one number",
    test: (password) => /[0-9]/.test(password),
  },
  {
    key: "special",
    label: "At least one special character",
    test: (password) => /[^A-Za-z0-9]/.test(password),
  },
];

export function getPasswordRuleStatus(password = "") {
  return PASSWORD_VALIDATION_RULES.map((rule) => ({
    ...rule,
    met: rule.test(password),
  }));
}

export function validateStrongPassword(password = "") {
  const failed = PASSWORD_VALIDATION_RULES.find((rule) => !rule.test(password));
  return failed ? failed.label : null;
}

export const MIN_REGISTRATION_AGE = 18;

function formatDateForInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Latest allowed DOB (YYYY-MM-DD) for a user to meet the minimum age. */
export function getMaxDateOfBirth(minAge = MIN_REGISTRATION_AGE) {
  const today = new Date();
  const maxBirthDate = new Date(
    today.getFullYear() - minAge,
    today.getMonth(),
    today.getDate()
  );
  return formatDateForInput(maxBirthDate);
}

export function isAtLeastMinAge(dateOfBirth, minAge = MIN_REGISTRATION_AGE) {
  if (!dateOfBirth) return false;

  const [year, month, day] = dateOfBirth.split("-").map(Number);
  if (!year || !month || !day) return false;

  const birthDate = new Date(year, month - 1, day);
  if (Number.isNaN(birthDate.getTime())) return false;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age >= minAge;
}

export function validateRegistrationField(key, config, value) {
  if (!config) return null;

  const trimmed =
    typeof value === "string" ? value.trim() : value != null ? String(value) : "";

  if (config.required && !trimmed) {
    return `${getRegistrationFieldLabel(key, config)} is required`;
  }

  if (!trimmed) return null;

  const minLength = config.min_length ?? config.minLength;
  const maxLength = config.max_length ?? config.maxLength;

  if (minLength != null && trimmed.length < minLength) {
    return `${getRegistrationFieldLabel(key, config)} must be at least ${minLength} characters`;
  }

  if (maxLength != null && trimmed.length > maxLength) {
    return `${getRegistrationFieldLabel(key, config)} must be at most ${maxLength} characters`;
  }

  if (key === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return "Please enter a valid email address";
  }

  if (key === "password") {
    const strongPasswordError = validateStrongPassword(value);
    if (strongPasswordError) return strongPasswordError;
  }

  if (key === "date_of_birth" && !isAtLeastMinAge(trimmed)) {
    return `You must be at least ${MIN_REGISTRATION_AGE} years old to register`;
  }

  return null;
}
